import React, { useEffect, useState } from "react";
import { 
  DollarSign, TrendingUp, Calendar, Users, 
  ArrowUpRight, ArrowDownRight, Wallet, CheckCircle, 
  Clock, Plus, Filter, Search, Download, 
  Percent, FileText, ChevronRight, X, Printer
} from "lucide-react";
import { 
  listFinancialTransactions, 
  createFinancialTransaction, 
  updateFinancialTransaction, 
  deleteFinancialTransaction, 
  listFinancialBatches, 
  createFinancialBatch, 
  payFinancialBatch,
  listClinicMembers,
  syncClinicAppointments,
  getClinicPaymentFees
} from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import { formatDate } from "@/lib/format";
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

const PAYMENT_METHODS = [
  { id: "pix", name: "Pix", defaultFee: 0.0 },
  { id: "credit_card", name: "Cartão de Crédito", defaultFee: 3.0 },
  { id: "debit_card", name: "Cartão de Débito", defaultFee: 1.5 },
  { id: "health_insurance", name: "Convênio", defaultFee: 10.0 },
  { id: "cash", name: "Dinheiro", defaultFee: 0.0 }
];

export default function Financeiro() {
  const { profile, clinicId, clinic } = useAuth();
  const myRole = clinic?.my_role || "professional"; // admin ou professional
  const isGestor = myRole === "admin";

  const [activeTab, setActiveTab] = useState("visao-geral");
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [clinicMembers, setClinicMembers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [clinicPaymentFees, setClinicPaymentFees] = useState({
    pix: 0.0, credit_card: 3.0, debit_card: 1.5,
    health_insurance: 10.0, cash: 0.0,
  });

  // Modais e Formulários
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Form Lançamento
  const [newTx, setNewTx] = useState({
    gross_amount: "",
    payment_method: "pix",
    gateway_fee_percent: 0.0,
    clinic_commission_percent: 30.0,
    competence_date: new Date().toISOString().substr(0, 10),
    received_at: new Date().toISOString().substr(0, 10),
    status: "received",
    patient_name: "",
    professional_id: profile?.id || "prof-1",
    professional_name: profile?.full_name || "Dr. Romulo Santos",
    notes: ""
  });

  // Form Fechamento de Repasse
  const [closingProfId, setClosingProfId] = useState("");
  const [closingStartDate, setClosingStartDate] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return start.toISOString().substr(0, 10);
  });
  const [closingEndDate, setClosingEndDate] = useState(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return end.toISOString().substr(0, 10);
  });
  const [previewTransactions, setPreviewTransactions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [closingNotes, setClosingNotes] = useState("");

  // Filtros de Lançamento (iniciam com o mês corrente por padrão)
  const [filterMethod, setFilterMethod] = useState("all");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return start.toISOString().substr(0, 10);
  });
  const [filterEndDate, setFilterEndDate] = useState(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return end.toISOString().substr(0, 10);
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const txParams = { clinic_id: clinicId };
      const batchParams = { clinic_id: clinicId };

      if (!isGestor) {
        txParams.professional_id = profile?.id;
        batchParams.professional_id = profile?.id;
      }

      const txList = await listFinancialTransactions(txParams);
      const batchList = await listFinancialBatches(batchParams);

      setTransactions(txList);
      setBatches(batchList);

      if (clinicId) {
        try {
          const members = await listClinicMembers(clinicId);
          setClinicMembers(members || []);
        } catch (memErr) {
          console.warn("Erro ao buscar membros da clinica:", memErr);
        }
        // Carregar taxas configuradas pela clínica
        try {
          const fees = await getClinicPaymentFees(clinicId);
          setClinicPaymentFees(fees);
        } catch (feeErr) {
          console.warn("Erro ao buscar taxas:", feeErr);
        }
      }

      // Pré-definir profissional padrão para fechamento
      if (txList.length > 0 && !closingProfId) {
        const firstProf = txList[0].professional_id;
        setClosingProfId(firstProf);
      }
    } catch (err) {
      console.error("Erro ao carregar dados financeiros:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [clinicId, myRole, profile?.id]);

  // Sempre que mudar o profissional selecionado ou as datas de fechamento, atualiza a prévia de repasse automaticamente
  useEffect(() => {
    if (activeTab === "repasses" && closingProfId) {
      const profTxs = transactions.filter(t => {
        const matchProf = t.professional_id === closingProfId;
        const matchBatch = !t.batch_id; // Não consolidada
        const matchDate = new Date(t.competence_date) >= new Date(closingStartDate + "T00:00:00") &&
                          new Date(t.competence_date) <= new Date(closingEndDate + "T23:59:59");
        return matchProf && matchBatch && matchDate;
      });
      setPreviewTransactions(profTxs);
      setShowPreview(true);
    } else if (!closingProfId) {
      setPreviewTransactions([]);
      setShowPreview(false);
    }
  }, [closingProfId, closingStartDate, closingEndDate, transactions, activeTab]);

  // Se o profissional logado (não gestor) possuir comissão configurada na clínica, preenche por padrão
  useEffect(() => {
    if (clinicMembers.length > 0 && profile?.id && !isGestor) {
      const myMem = clinicMembers.find(m => m.profile_id === profile.id);
      if (myMem) {
        const myComm = myMem.commission_percent !== undefined && myMem.commission_percent !== null 
          ? Number(myMem.commission_percent) 
          : 30.00;
        setNewTx(prev => ({
          ...prev,
          clinic_commission_percent: myComm
        }));
      }
    }
  }, [clinicMembers, profile?.id, isGestor]);

  // Quando as taxas da clínica carregarem, atualiza a taxa padrão do form
  useEffect(() => {
    const currentMethod = newTx.payment_method || "pix";
    const fee = clinicPaymentFees[currentMethod];
    if (fee !== undefined) {
      setNewTx(prev => ({ ...prev, gateway_fee_percent: fee }));
    }
  }, [clinicPaymentFees]);

  // Calcula valores dinâmicos ao digitar no Lançamento
  const handleTxChange = (field, val) => {
    let updated = { ...newTx, [field]: val };
    
    if (field === "payment_method") {
      // Usar taxa configurada pela clínica; fallback para o valor do PAYMENT_METHODS
      const configuredFee = clinicPaymentFees[val];
      const methodDefault = PAYMENT_METHODS.find(m => m.id === val)?.defaultFee ?? 0.0;
      updated.gateway_fee_percent = configuredFee !== undefined ? configuredFee : methodDefault;
    }

    setNewTx(updated);
  };

  // Salva Novo Lançamento
  const handleSaveTx = async (e) => {
    e.preventDefault();
    const gross = Number(newTx.gross_amount);
    if (isNaN(gross) || gross <= 0) return alert("Insira um valor bruto válido");

    const feePercent = Number(newTx.gateway_fee_percent);
    const commPercent = Number(newTx.clinic_commission_percent);

    const feeAmount = gross * (feePercent / 100);
    const netAfterFees = gross - feeAmount;
    const commAmount = gross * (commPercent / 100);
    const professionalAmount = gross - feeAmount - commAmount;

    const payload = {
      clinic_id: clinicId || "clinic-default",
      professional_id: newTx.professional_id,
      professional_name: newTx.professional_name,
      patient_name: newTx.patient_name || "Paciente Avulso",
      gross_amount: gross,
      payment_method: newTx.payment_method,
      gateway_fee_percent: feePercent,
      gateway_fee_amount: Number(feeAmount.toFixed(2)),
      net_amount_after_fees: Number(netAfterFees.toFixed(2)),
      clinic_commission_percent: commPercent,
      clinic_commission_amount: Number(commAmount.toFixed(2)),
      professional_amount: Number(professionalAmount.toFixed(2)),
      competence_date: new Date(newTx.competence_date).toISOString(),
      received_at: newTx.status === "received" ? new Date(newTx.received_at).toISOString() : null,
      status: newTx.status,
      notes: newTx.notes,
      batch_id: null
    };

    try {
      await createFinancialTransaction(payload);
      setShowAddModal(false);
      // Limpar formulário — usa taxa da clínica para pix (padrão)
      setNewTx({
        gross_amount: "",
        payment_method: "pix",
        gateway_fee_percent: clinicPaymentFees["pix"] ?? 0.0,
        clinic_commission_percent: 30.0,
        competence_date: new Date().toISOString().substr(0, 10),
        received_at: new Date().toISOString().substr(0, 10),
        status: "received",
        patient_name: "",
        professional_id: profile?.id || "prof-1",
        professional_name: profile?.full_name || "Dr. Romulo Santos",
        notes: ""
      });
      loadData();
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar lançamento financeiro");
    }
  };

  // Excluir lançamento
  const handleDeleteTx = async (id) => {
    if (!window.confirm("Deseja realmente excluir este lançamento financeiro?")) return;
    try {
      await deleteFinancialTransaction(id);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  // Sincronizar Consultas Existentes de forma retroativa
  const handleSyncAppointments = async () => {
    if (!clinicId) return alert("Erro: ID da clínica não encontrado!");
    setSyncing(true);
    try {
      const result = await syncClinicAppointments(clinicId);
      if (result.success) {
        alert(`Sincronização concluída! ${result.syncedCount} novas consultas foram faturadas e integradas com sucesso.`);
        await loadData();
      } else {
        alert("Houve um erro na sincronização: " + (result.error || "Erro desconhecido"));
      }
    } catch (err) {
      console.error("Erro ao sincronizar consultas:", err);
      alert("Erro na sincronização: " + err.message);
    } finally {
      setSyncing(false);
    }
  };

  // Consolidação de Prévia de Fechamento de Repasse
  const handlePreviewClosing = () => {
    const profTxs = transactions.filter(t => {
      const matchProf = t.professional_id === closingProfId;
      const matchBatch = !t.batch_id; // Não consolidada
      const matchDate = new Date(t.competence_date) >= new Date(closingStartDate + "T00:00:00") &&
                        new Date(t.competence_date) <= new Date(closingEndDate + "T23:59:59");
      return matchProf && matchBatch && matchDate;
    });

    setPreviewTransactions(profTxs);
    setShowPreview(true);
  };

  // Criação Oficial do Lote de Repasse
  const handleCreateBatch = async () => {
    if (previewTransactions.length === 0) return alert("Nenhum lançamento selecionado para fechamento");

    const totalGross = previewTransactions.reduce((acc, t) => acc + t.gross_amount, 0);
    const totalFees = previewTransactions.reduce((acc, t) => acc + t.gateway_fee_amount, 0);
    const totalClinic = previewTransactions.reduce((acc, t) => acc + t.clinic_commission_amount, 0);
    const totalProfessional = previewTransactions.reduce((acc, t) => acc + t.professional_amount, 0);

    const professionalName = previewTransactions[0].professional?.full_name || previewTransactions[0].professional_name || "Dr. Parceiro";

    const payload = {
      clinic_id: clinicId || "clinic-default",
      professional_id: closingProfId,
      professional_name: professionalName,
      start_date: new Date(closingStartDate).toISOString(),
      end_date: new Date(closingEndDate).toISOString(),
      total_gross: totalGross,
      total_fees: totalFees,
      total_clinic: totalClinic,
      total_professional: totalProfessional,
      notes: closingNotes || `Competência de ${formatDate(new Date(closingStartDate).toISOString(), { month: "short", year: "numeric" })}`,
      transaction_ids: previewTransactions.map(t => t.id)
    };

    try {
      await createFinancialBatch(payload);
      setShowPreview(false);
      setPreviewTransactions([]);
      setClosingNotes("");
      loadData();
      alert("Lote de repasse fechado com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao fechar lote de repasse");
    }
  };

  // Efetuar pagamento do repasse
  const handlePayBatch = async (batchId) => {
    if (!window.confirm("Confirmar o pagamento deste lote de repasse médico?")) return;
    try {
      await payFinancialBatch(batchId);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  // Imprimir Recibo/Extrato de lote
  const handlePrintReceipt = (batch) => {
    // Buscar as transações associadas ao lote
    const batchTxs = transactions.filter(t => t.batch_id === batch.id);
    setSelectedReceipt({ batch, txs: batchTxs });
    setShowReceiptModal(true);
  };

  // Cálculos Estatísticos para Dashboards baseados no Período Selecionado
  const getDashboardStats = () => {
    const filteredForStats = transactions.filter(t => {
      const competence = new Date(t.competence_date);
      const matchStart = filterStartDate === "" || competence >= new Date(filterStartDate + "T00:00:00");
      const matchEnd = filterEndDate === "" || competence <= new Date(filterEndDate + "T23:59:59");
      return matchStart && matchEnd;
    });

    const grossTotal = filteredForStats.reduce((acc, t) => acc + t.gross_amount, 0);
    const feesTotal = filteredForStats.reduce((acc, t) => acc + t.gateway_fee_amount, 0);
    const clinicTotal = filteredForStats.reduce((acc, t) => acc + t.clinic_commission_amount, 0);
    
    // Filtro de lotes de repasse pelo período
    const filteredBatches = batches.filter(b => {
      const batchDate = new Date(b.created_at);
      const matchStart = filterStartDate === "" || batchDate >= new Date(filterStartDate + "T00:00:00");
      const matchEnd = filterEndDate === "" || batchDate <= new Date(filterEndDate + "T23:59:59");
      return matchStart && matchEnd;
    });

    const paidRepasses = filteredBatches.filter(b => b.status === "paid").reduce((acc, b) => acc + b.total_professional, 0);
    const pendingRepasses = filteredBatches.filter(b => b.status !== "paid").reduce((acc, b) => acc + b.total_professional, 0);
    
    // Transações não vinculadas a lotes ainda dentro do período
    const unbatchedProfessionalTotal = filteredForStats.filter(t => !t.batch_id).reduce((acc, t) => acc + t.professional_amount, 0);
    const totalToRepay = pendingRepasses + unbatchedProfessionalTotal;

    return {
      grossTotal,
      feesTotal,
      clinicTotal,
      paidRepasses,
      totalToRepay
    };
  };

  const stats = getDashboardStats();

  const filteredTxsForPeriod = transactions.filter(t => {
    const competence = new Date(t.competence_date);
    const matchStart = filterStartDate === "" || competence >= new Date(filterStartDate + "T00:00:00");
    const matchEnd = filterEndDate === "" || competence <= new Date(filterEndDate + "T23:59:59");
    return matchStart && matchEnd;
  });

  // Gráficos Recharts Configs baseados no Período Selecionado
  const paymentMethodChartData = PAYMENT_METHODS.map(method => {
    const total = filteredTxsForPeriod
      .filter(t => t.payment_method === method.id)
      .reduce((acc, t) => acc + t.gross_amount, 0);
    return { name: method.name, value: total };
  }).filter(item => item.value > 0);

  const monthlyChartData = (() => {
    const monthlyMap = {};
    filteredTxsForPeriod.forEach(t => {
      const date = new Date(t.competence_date);
      const label = date.toLocaleDateString("pt-BR", { month: "short", year: "numeric" });
      if (!monthlyMap[label]) {
        monthlyMap[label] = { month: label, faturamento: 0, repasse: 0, lucro: 0 };
      }
      monthlyMap[label].faturamento += t.gross_amount;
      monthlyMap[label].repasse += t.professional_amount;
      monthlyMap[label].lucro += t.clinic_commission_amount;
    });
    return Object.values(monthlyMap).reverse();
  })();

  const COLORS = ["#2D6A4F", "#52B788", "#74C69D", "#D8F3DC", "#B7B7A4"];

  // Filtros aplicados em Lançamentos
  const filteredTxs = transactions.filter(t => {
    const matchMethod = filterMethod === "all" || t.payment_method === filterMethod;
    const matchSearch = filterSearch === "" || 
      (t.patient_name || "").toLowerCase().includes(filterSearch.toLowerCase()) ||
      (t.professional_name || "").toLowerCase().includes(filterSearch.toLowerCase()) ||
      (t.notes || "").toLowerCase().includes(filterSearch.toLowerCase());
    
    const competence = new Date(t.competence_date);
    const matchStart = filterStartDate === "" || competence >= new Date(filterStartDate + "T00:00:00");
    const matchEnd = filterEndDate === "" || competence <= new Date(filterEndDate + "T23:59:59");

    return matchMethod && matchSearch && matchStart && matchEnd;
  });

  // Lista unificada de profissionais na clínica para fechar repasses (membros cadastrados + profissionais com transações)
  const dropdownProfessionals = (() => {
    const map = new Map();
    // 1. Adicionar do clinicMembers
    clinicMembers.forEach(m => {
      if (m.profile_id && m.profile?.full_name) {
        map.set(m.profile_id, m.profile.full_name);
      }
    });
    // 2. Adicionar das transações
    transactions.forEach(t => {
      if (t.professional_id) {
        const name = t.professional_name || t.professional?.full_name || "Profissional";
        map.set(t.professional_id, name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  })();

  return (
    <div className="ds-fade-up">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <PageHeader 
          eyebrow="Controle Clínico" 
          title="Gestão Financeira" 
          description={isGestor ? "Monitore faturamento global, lucros retidos, repasses médicos e conciliação de adquirentes." : "Monitore seus atendimentos, conta corrente individual e extratos de repasses."} 
        />
        {isGestor && (
          <div className="flex flex-wrap gap-2 md:mt-0">
            <button 
              onClick={handleSyncAppointments}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all flex items-center gap-1.5 shadow-sm disabled:opacity-50"
            >
              <TrendingUp className="w-4 h-4" /> 
              {syncing ? "Sincronizando..." : "Sincronizar Consultas"}
            </button>
            <button 
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 text-sm font-medium bg-brand hover:bg-brand/90 text-white rounded-lg transition-all flex items-center gap-1.5 shadow-sm"
            >
              <Plus className="w-4 h-4" /> Novo Lançamento
            </button>
          </div>
        )}
      </div>

      {/* Navegação por Abas */}
      <div className="flex border-b border-border mb-6 overflow-x-auto whitespace-nowrap no-scrollbar -mx-4 px-4">
        <button 
          onClick={() => setActiveTab("visao-geral")}
          className={`py-3 px-4 border-b-2 font-medium text-sm transition-all flex items-center gap-2 ${activeTab === "visao-geral" ? "border-brand text-brand font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          <TrendingUp className="w-4 h-4" />
          Visão Geral
        </button>
        <button 
          onClick={() => setActiveTab("lancamentos")}
          className={`py-3 px-4 border-b-2 font-medium text-sm transition-all flex items-center gap-2 ${activeTab === "lancamentos" ? "border-brand text-brand font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          <Wallet className="w-4 h-4" />
          Contas a Receber
        </button>
        {isGestor && (
          <button 
            onClick={() => setActiveTab("repasses")}
            className={`py-3 px-4 border-b-2 font-medium text-sm transition-all flex items-center gap-2 ${activeTab === "repasses" ? "border-brand text-brand font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Users className="w-4 h-4" />
            Gestão de Repasses
          </button>
        )}
      </div>

      {!loading && (
        <div className="mb-6 p-4 rounded-2xl bg-white border border-[hsl(var(--border-default))] flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm bg-card-glass ds-fade-up">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[hsl(var(--brand-light))]/30 rounded-xl text-[hsl(var(--brand))]">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] uppercase tracking-wider">Período de Análise Financeira</div>
              <div className="text-sm font-bold text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {filterStartDate ? formatDate(filterStartDate + "T12:00:00") : "Início"} até {filterEndDate ? formatDate(filterEndDate + "T12:00:00") : "Fim"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="date" 
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="ds-input py-1.5 text-xs max-w-[140px] font-medium"
            />
            <span className="text-muted-foreground text-xs font-semibold">a</span>
            <input 
              type="date" 
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="ds-input py-1.5 text-xs max-w-[140px] font-medium"
            />
            <button
              onClick={() => {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substr(0, 10);
                const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substr(0, 10);
                setFilterStartDate(start);
                setFilterEndDate(end);
              }}
              className="px-3 py-2 text-xs font-bold bg-[hsl(var(--bg-hover))] hover:bg-[hsl(var(--border-default))] text-[hsl(var(--text-secondary))] rounded-lg transition-all"
              title="Redefinir para o mês corrente"
            >
              Mês Corrente
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mb-4"></div>
          Carregando informações financeiras...
        </div>
      ) : (
        <>
          {/* TAB 1: VISÃO GERAL */}
          {activeTab === "visao-geral" && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="ds-card p-5 relative overflow-hidden bg-card-glass backdrop-blur-md">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      {isGestor ? "Faturamento Bruto" : "Minha Produção Bruta"}
                    </span>
                    <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-500">
                      <DollarSign className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    R$ {stats.grossTotal.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
                    Total acumulado no período
                  </div>
                </div>

                <div className="ds-card p-5 relative overflow-hidden bg-card-glass backdrop-blur-md">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      {isGestor ? "Lucro Retido Clínica" : "Comissão da Clínica"}
                    </span>
                    <div className="p-2 bg-brand/10 rounded-lg text-brand">
                      <Percent className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    R$ {stats.clinicTotal.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <ArrowDownRight className="w-3.5 h-3.5 text-brand" />
                    Comissionamento operacional ({isGestor ? "global" : "individual"})
                  </div>
                </div>

                <div className="ds-card p-5 relative overflow-hidden bg-card-glass backdrop-blur-md">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      {isGestor ? "A Repassar (Líquido)" : "Saldo a Receber"}
                    </span>
                    <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500">
                      <Wallet className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-foreground text-blue-600 dark:text-blue-400">
                    R$ {stats.totalToRepay.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-blue-500" />
                    R$ {stats.paidRepasses.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} já repassados
                  </div>
                </div>
              </div>

              {/* Gráficos */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Gráfico Mensal */}
                <div className="lg:col-span-2 ds-card p-5 bg-card-glass">
                  <h3 className="text-base font-semibold mb-4 text-foreground">Comparativo de Competências</h3>
                  <div className="h-64">
                    {monthlyChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyChartData}>
                          <XAxis dataKey="month" stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                          <YAxis stroke="#888888" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
                          <Tooltip formatter={(v) => `R$ ${v.toFixed(2)}`} contentStyle={{ borderRadius: "8px", border: "1px solid #E2E8F0" }} />
                          <Legend verticalAlign="top" height={36} iconType="circle" />
                          <Bar dataKey="faturamento" name="Faturamento Bruto" fill="#2D6A4F" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="repasse" name={isGestor ? "Repasse Médicos" : "Meu Repasse"} fill="#52B788" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground text-sm">Nenhum lançamento no período</div>
                    )}
                  </div>
                </div>

                {/* Formas de Pagamento */}
                <div className="ds-card p-5 bg-card-glass flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-semibold mb-2 text-foreground">Meios de Pagamento</h3>
                    <p className="text-xs text-muted-foreground mb-4">Divisão do faturamento bruto por adquirente.</p>
                  </div>
                  <div className="h-44 flex items-center justify-center">
                    {paymentMethodChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={paymentMethodChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={65}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {paymentMethodChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => `R$ ${v.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-muted-foreground text-sm">Sem faturamento</div>
                    )}
                  </div>
                  <div className="mt-4 space-y-1.5 max-h-32 overflow-y-auto pr-1">
                    {paymentMethodChartData.map((entry, idx) => (
                      <div key={idx} className="flex justify-between items-center text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                          <span className="text-muted-foreground">{entry.name}</span>
                        </div>
                        <span className="font-semibold text-foreground">R$ {entry.value.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Lotes Recentes */}
              <div className="ds-card p-5 bg-card-glass">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-base font-semibold text-foreground">
                    {isGestor ? "Lotes de Repasse dos Médicos" : "Meus Extratos de Repasse"}
                  </h3>
                  <span className="text-xs text-muted-foreground">Mostrando os últimos lançamentos</span>
                </div>
                {batches.length > 0 ? (
                  <div className="overflow-x-auto -mx-5 px-5">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground font-medium text-xs uppercase">
                          <th className="py-2.5">Período</th>
                          {isGestor && <th className="py-2.5">Profissional</th>}
                          <th className="py-2.5">Bruto</th>
                          <th className="py-2.5">Líquido</th>
                          <th className="py-2.5">Status</th>
                          <th className="py-2.5 text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.slice(0, 5).map((batch) => (
                          <tr key={batch.id} className="border-b border-border hover:bg-muted/30 transition-all">
                            <td className="py-3.5">
                              <div className="font-medium text-foreground">{batch.notes}</div>
                              <div className="text-xs text-muted-foreground">
                                {formatDate(batch.start_date)} a {formatDate(batch.end_date)}
                              </div>
                            </td>
                            {isGestor && (
                              <td className="py-3.5 text-muted-foreground font-medium">
                                {batch.professional?.full_name || batch.professional_name}
                              </td>
                            )}
                            <td className="py-3.5 text-muted-foreground">R$ {batch.total_gross.toFixed(2)}</td>
                            <td className="py-3.5 font-semibold text-foreground">R$ {batch.total_professional.toFixed(2)}</td>
                            <td className="py-3.5">
                              {batch.status === "paid" ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                                  <CheckCircle className="w-3 h-3" /> Pago
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/20">
                                  <Clock className="w-3 h-3" /> Pendente
                                </span>
                              )}
                            </td>
                            <td className="py-3.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button 
                                  onClick={() => handlePrintReceipt(batch)}
                                  className="p-1 text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 rounded transition-all"
                                  title="Ver Extrato"
                                >
                                  <FileText className="w-4 h-4" />
                                </button>
                                {isGestor && batch.status !== "paid" && (
                                  <button 
                                    onClick={() => handlePayBatch(batch.id)}
                                    className="px-2.5 py-1 text-xs font-medium text-white bg-brand hover:bg-brand/90 rounded shadow-sm transition-all"
                                  >
                                    Pagar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-10 text-muted-foreground text-sm">Nenhum lote consolidado gerado.</div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: LANÇAMENTOS (RECEITAS) */}
          {activeTab === "lancamentos" && (
            <div className="space-y-4">
              {/* Barra de Filtros e Cadastro */}
              <div className="flex flex-col md:flex-row gap-3 justify-between items-start md:items-center bg-card-glass p-4 rounded-xl border border-border">
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                  {/* Busca */}
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input 
                      type="text" 
                      placeholder="Pesquisar paciente, médico ou notas..." 
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      className="ds-input pl-9 pr-4 py-1.5 text-sm w-full"
                    />
                  </div>

                  {/* Método */}
                  <select
                    value={filterMethod}
                    onChange={(e) => setFilterMethod(e.target.value)}
                    className="ds-input py-1.5 text-sm font-medium text-muted-foreground focus:text-foreground"
                  >
                    <option value="all">Meio Pagamento (Todos)</option>
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 w-full md:w-auto justify-end">
                  <button 
                    onClick={() => {
                      setFilterSearch("");
                      setFilterMethod("all");
                    }}
                    className="px-3 py-1.5 text-xs font-semibold bg-muted hover:bg-muted/80 rounded-lg text-muted-foreground transition-all flex items-center gap-1.5"
                  >
                    <Filter className="w-3.5 h-3.5" /> Limpar Filtros
                  </button>
                </div>
              </div>

              {/* Tabela de Lançamentos */}
              <div className="ds-card p-5 bg-card-glass">
                {filteredTxs.length > 0 ? (
                  <div className="overflow-x-auto -mx-5 px-5">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground font-medium text-xs uppercase">
                          <th className="py-2.5">Competência</th>
                          <th className="py-2.5">Paciente</th>
                          <th className="py-2.5">Executante</th>
                          <th className="py-2.5">Meio de Pagamento</th>
                          <th className="py-2.5">Valor Bruto</th>
                          <th className="py-2.5">Taxas / Comissão</th>
                          <th className="py-2.5">Líquido Repasse</th>
                          {isGestor && <th className="py-2.5 text-right">Ações</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTxs.map((tx) => (
                          <tr key={tx.id} className="border-b border-border hover:bg-muted/30 transition-all">
                            <td className="py-3.5">
                              <div className="font-semibold text-foreground">{formatDate(tx.competence_date)}</div>
                              <div className="text-xs text-muted-foreground">
                                {tx.received_at ? `Recebido em: ${formatDate(tx.received_at)}` : "A receber"}
                              </div>
                            </td>
                            <td className="py-3.5">
                              <div className="font-medium text-foreground">{tx.patient?.full_name || tx.patient_name || "Paciente Avulso"}</div>
                              {tx.notes && <div className="text-xs text-muted-foreground italic truncate max-w-xs">{tx.notes}</div>}
                            </td>
                            <td className="py-3.5 text-muted-foreground">
                              {tx.professional?.full_name || tx.professional_name}
                            </td>
                            <td className="py-3.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground capitalize">
                                {PAYMENT_METHODS.find(m => m.id === tx.payment_method)?.name || tx.payment_method}
                              </span>
                            </td>
                            <td className="py-3.5 text-muted-foreground">
                              R$ {tx.gross_amount.toFixed(2)}
                            </td>
                            <td className="py-3.5 text-xs text-muted-foreground">
                              <div>Cartão: R$ {tx.gateway_fee_amount.toFixed(2)} ({tx.gateway_fee_percent}%)</div>
                              <div>Clínica: R$ {tx.clinic_commission_amount.toFixed(2)} ({tx.clinic_commission_percent}%)</div>
                            </td>
                            <td className="py-3.5 font-bold text-brand">
                              R$ {tx.professional_amount.toFixed(2)}
                            </td>
                            {isGestor && (
                              <td className="py-3.5 text-right">
                                <button 
                                  onClick={() => handleDeleteTx(tx.id)}
                                  disabled={!!tx.batch_id}
                                  className={`p-1.5 rounded transition-all ${tx.batch_id ? "text-muted/40 cursor-not-allowed" : "text-brand hover:bg-brand/10 hover:text-brand"}`}
                                  title={tx.batch_id ? "Bloqueado - Vinculado a lote consolidado" : "Excluir lançamento"}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground text-sm">
                    Nenhum lançamento financeiro localizado com os filtros aplicados.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: GESTÃO DE REPASSES */}
          {activeTab === "repasses" && isGestor && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Painel de Fechamento */}
              <div className="lg:col-span-1 ds-card p-5 bg-card-glass h-fit space-y-4">
                <div className="border-b border-border pb-3">
                  <h3 className="text-base font-semibold text-foreground">Consolidar Fechamento</h3>
                  <p className="text-xs text-muted-foreground">Selecione o profissional parceiro e as datas das consultas a repassar.</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Médico Parceiro</label>
                    <select
                      value={closingProfId}
                      onChange={(e) => setClosingProfId(e.target.value)}
                      className="ds-input w-full font-medium"
                    >
                      <option value="">Selecione o Profissional...</option>
                      {dropdownProfessionals.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Data Início</label>
                      <input 
                        type="date" 
                        value={closingStartDate}
                        onChange={(e) => setClosingStartDate(e.target.value)}
                        className="ds-input w-full font-medium text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Data Fim</label>
                      <input 
                        type="date" 
                        value={closingEndDate}
                        onChange={(e) => setClosingEndDate(e.target.value)}
                        className="ds-input w-full font-medium text-xs"
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex items-center justify-center gap-1.5 text-[10px] font-bold text-[hsl(var(--success-text))] bg-[hsl(var(--success-bg))]/55 px-3 py-2 rounded-lg border border-[hsl(var(--success-text))]/10">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    Busca Automática em Tempo Real
                  </div>
                </div>
              </div>

              {/* Prévia de Consolidação */}
              <div className="lg:col-span-2 space-y-4">
                {showPreview ? (
                  <div className="ds-card p-5 bg-card-glass space-y-4">
                    <div className="flex justify-between items-center border-b border-border pb-3">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">Extrato Consolidado da Competência</h3>
                        <p className="text-xs text-muted-foreground">Prévia para repasse líquido do profissional.</p>
                      </div>
                      <button 
                        onClick={() => setShowPreview(false)}
                        className="p-1 hover:bg-muted rounded text-muted-foreground transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {previewTransactions.length > 0 ? (
                      <div className="space-y-4">
                        <div className="max-h-56 overflow-y-auto border border-border rounded-lg">
                          <table className="w-full text-left text-xs whitespace-nowrap">
                            <thead className="bg-muted text-muted-foreground font-semibold uppercase sticky top-0">
                              <tr>
                                <th className="p-2.5">Data</th>
                                <th className="p-2.5">Paciente</th>
                                <th className="p-2.5">Valor Bruto</th>
                                <th className="p-2.5">Taxas Cartão</th>
                                <th className="p-2.5">Comissão Clínica</th>
                                <th className="p-2.5 font-bold">Líquido Médico</th>
                              </tr>
                            </thead>
                            <tbody>
                              {previewTransactions.map((t) => (
                                <tr key={t.id} className="border-b border-border hover:bg-muted/10">
                                  <td className="p-2.5">{formatDate(t.competence_date)}</td>
                                  <td className="p-2.5 font-medium text-foreground">{t.patient?.full_name || t.patient_name}</td>
                                  <td className="p-2.5 text-muted-foreground">R$ {t.gross_amount.toFixed(2)}</td>
                                  <td className="p-2.5 text-muted-foreground">R$ {t.gateway_fee_amount.toFixed(2)}</td>
                                  <td className="p-2.5 text-muted-foreground">R$ {t.clinic_commission_amount.toFixed(2)}</td>
                                  <td className="p-2.5 font-bold text-brand">R$ {t.professional_amount.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Sumário */}
                        <div className="bg-muted/50 p-4 rounded-lg space-y-2 border border-border">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Faturamento Bruto:</span>
                            <span className="font-semibold text-foreground">
                              R$ {previewTransactions.reduce((acc, t) => acc + t.gross_amount, 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Total de Taxas de Cartão:</span>
                            <span className="font-semibold text-foreground text-brand">
                              - R$ {previewTransactions.reduce((acc, t) => acc + t.gateway_fee_amount, 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Total de Comissão Clínica:</span>
                            <span className="font-semibold text-foreground text-brand">
                              - R$ {previewTransactions.reduce((acc, t) => acc + t.clinic_commission_amount, 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-base border-t border-border pt-2 font-bold">
                            <span className="text-foreground">Líquido Final do Médico:</span>
                            <span className="text-brand text-lg">
                              R$ {previewTransactions.reduce((acc, t) => acc + t.professional_amount, 0).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Notas do Lote */}
                        <div>
                          <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Notas do Fechamento (Opcional)</label>
                          <textarea
                            placeholder="Adicione orientações de transferência, comprovante, etc..."
                            value={closingNotes}
                            onChange={(e) => setClosingNotes(e.target.value)}
                            className="ds-input w-full h-16 resize-none py-2 text-sm"
                          />
                        </div>

                        <button
                          onClick={handleCreateBatch}
                          className="w-full py-2.5 bg-brand hover:bg-brand/90 text-white rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-1.5 shadow-sm"
                        >
                          <CheckCircle className="w-4 h-4" /> Gerar Lote de Repasse e Fechar Fatura
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-10 text-muted-foreground text-sm">
                        Nenhum atendimento pendente de repasse localizado para este profissional neste intervalo de datas.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ds-card p-10 bg-card-glass flex flex-col items-center justify-center text-muted-foreground border border-dashed border-border">
                    <Wallet className="w-12 h-12 text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-medium">Consolidador de Competências</p>
                    <p className="text-xs text-center max-w-sm mt-1">Preencha o formulário ao lado e clique em buscar atendimentos para abrir a prévia matemática e gerar o extrato líquido de repasses médicos.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* MODAL: NOVO LANÇAMENTO */}
      {showAddModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="ds-card w-full max-w-lg bg-card-glass border border-border shadow-2xl relative flex flex-col p-6 ds-fade-up">
            <button 
              onClick={() => setShowAddModal(false)}
              className="absolute right-4 top-4 p-1 hover:bg-muted rounded text-muted-foreground transition-all"
            >
              <X className="w-4 h-4" />
            </button>

            <h3 className="text-base font-bold text-foreground mb-1">Novo Lançamento Financeiro</h3>
            <p className="text-xs text-muted-foreground mb-4">Adicione uma receita manual ao contas a receber da clínica.</p>

            <form onSubmit={handleSaveTx} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Paciente</label>
                  <input 
                    type="text" 
                    placeholder="Nome do paciente" 
                    value={newTx.patient_name}
                    onChange={(e) => handleTxChange("patient_name", e.target.value)}
                    className="ds-input w-full text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Médico Executante</label>
                  {isGestor && clinicMembers.length > 0 ? (
                    <select
                      value={newTx.professional_id}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const member = clinicMembers.find(m => m.profile_id === selectedId);
                        const name = member?.profile?.full_name || "";
                        const comm = member?.commission_percent !== undefined && member?.commission_percent !== null 
                          ? Number(member.commission_percent) 
                          : 30.00;
                        
                        setNewTx(prev => ({
                          ...prev,
                          professional_id: selectedId,
                          professional_name: name,
                          clinic_commission_percent: comm
                        }));
                      }}
                      className="ds-input w-full text-sm"
                      required
                    >
                      <option value="">Selecione o profissional...</option>
                      {clinicMembers.map(m => (
                        <option key={m.profile_id} value={m.profile_id}>
                          {m.profile?.full_name || "Médico sem nome"}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input 
                      type="text" 
                      placeholder="Dr. Executante" 
                      value={newTx.professional_name}
                      onChange={(e) => handleTxChange("professional_name", e.target.value)}
                      className="ds-input w-full text-sm"
                      required
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Valor Bruto (R$)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    placeholder="R$ 0,00" 
                    value={newTx.gross_amount}
                    onChange={(e) => handleTxChange("gross_amount", e.target.value)}
                    className="ds-input w-full text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Forma de Pagamento</label>
                  <select
                    value={newTx.payment_method}
                    onChange={(e) => handleTxChange("payment_method", e.target.value)}
                    className="ds-input w-full text-sm"
                  >
                    {PAYMENT_METHODS.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 bg-muted/40 p-3 rounded-lg border border-border">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Taxa Cartão/Gateway (%)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    value={newTx.gateway_fee_percent}
                    onChange={(e) => handleTxChange("gateway_fee_percent", Number(e.target.value))}
                    className="ds-input w-full text-sm bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Comissão Clínica (%)</label>
                  <input 
                    type="number" 
                    step="0.5"
                    value={newTx.clinic_commission_percent}
                    onChange={(e) => handleTxChange("clinic_commission_percent", Number(e.target.value))}
                    className="ds-input w-full text-sm bg-background"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Competência (Consulta)</label>
                  <input 
                    type="date" 
                    value={newTx.competence_date}
                    onChange={(e) => handleTxChange("competence_date", e.target.value)}
                    className="ds-input w-full text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Data de Recebimento</label>
                  <input 
                    type="date" 
                    value={newTx.received_at}
                    onChange={(e) => handleTxChange("received_at", e.target.value)}
                    className="ds-input w-full text-sm"
                    disabled={newTx.status !== "received"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Status Recebimento</label>
                  <select
                    value={newTx.status}
                    onChange={(e) => handleTxChange("status", e.target.value)}
                    className="ds-input w-full text-sm"
                  >
                    <option value="received">Recebido / Compensado</option>
                    <option value="pending">Pendente / Cartão Futuro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1">Notas / Observação</label>
                  <input 
                    type="text" 
                    placeholder="Ex: Nota fiscal, PIX ref..." 
                    value={newTx.notes}
                    onChange={(e) => handleTxChange("notes", e.target.value)}
                    className="ds-input w-full text-sm"
                  />
                </div>
              </div>

              {/* Demonstrativo em tempo real */}
              {Number(newTx.gross_amount) > 0 && (
                <div className="bg-brand/5 border border-brand/20 p-3 rounded-lg text-xs space-y-1">
                  <div className="font-semibold text-brand mb-1">Demonstrativo da Transação (Matemática Real):</div>
                  <div className="flex justify-between">
                    <span>Bruto:</span>
                    <span>R$ {Number(newTx.gross_amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>(-) Taxa Gateway ({newTx.gateway_fee_percent}%):</span>
                    <span>- R$ {(Number(newTx.gross_amount) * (newTx.gateway_fee_percent / 100)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>(-) Retenção Clínica ({newTx.clinic_commission_percent}%):</span>
                    <span>- R$ {(Number(newTx.gross_amount) * (newTx.clinic_commission_percent / 100)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-foreground border-t border-border pt-1.5 mt-1 text-sm">
                    <span>Líquido Repasse Médico:</span>
                    <span className="text-brand">
                      R$ {(Number(newTx.gross_amount) - (Number(newTx.gross_amount) * (newTx.gateway_fee_percent / 100)) - (Number(newTx.gross_amount) * (newTx.clinic_commission_percent / 100))).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <button 
                type="submit"
                className="w-full py-2.5 bg-brand hover:bg-brand/90 text-white rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-1.5 shadow-sm"
              >
                <CheckCircle className="w-4 h-4" /> Registrar Lançamento
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: VISUALIZAR E IMPRIMIR EXTRATO DO LOTE */}
      {showReceiptModal && selectedReceipt && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="ds-card w-full max-w-2xl bg-card-glass border border-border shadow-2xl relative flex flex-col p-6 ds-fade-up">
            <button 
              onClick={() => setShowReceiptModal(false)}
              className="absolute right-4 top-4 p-1 hover:bg-muted rounded text-muted-foreground transition-all"
            >
              <X className="w-4 h-4" />
            </button>

            <div id="print-area" className="space-y-6 pr-1 max-h-[75vh] overflow-y-auto">
              <div className="flex justify-between items-start border-b border-border pb-4">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Extrato de Repasse Médico</h2>
                  <p className="text-xs text-muted-foreground">ID do Lote: #{selectedReceipt.batch.id}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${selectedReceipt.batch.status === "paid" ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20" : "bg-blue-500/10 text-blue-500 border border-blue-500/20"}`}>
                    {selectedReceipt.batch.status === "paid" ? "Pago" : "Pendente"}
                  </span>
                  {selectedReceipt.batch.paid_at && (
                    <p className="text-xxs text-muted-foreground mt-1">Lançado: {formatDate(selectedReceipt.batch.paid_at)}</p>
                  )}
                </div>
              </div>

              {/* Informações */}
              <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg border border-border text-xs">
                <div>
                  <p className="text-muted-foreground uppercase font-semibold">Profissional Beneficiário</p>
                  <p className="font-bold text-foreground text-sm mt-0.5">
                    {selectedReceipt.batch.professional?.full_name || selectedReceipt.batch.professional_name}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground uppercase font-semibold">Competência Fechada</p>
                  <p className="font-bold text-foreground text-sm mt-0.5">
                    {formatDate(selectedReceipt.batch.start_date)} a {formatDate(selectedReceipt.batch.end_date)}
                  </p>
                </div>
              </div>

              {/* Discriminativo */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Procedimentos Consolidados</h4>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted text-muted-foreground font-semibold">
                      <tr>
                        <th className="p-2 border-b border-border">Data</th>
                        <th className="p-2 border-b border-border">Paciente</th>
                        <th className="p-2 border-b border-border text-right">Bruto</th>
                        <th className="p-2 border-b border-border text-right">Taxa Cartão</th>
                        <th className="p-2 border-b border-border text-right">Comissão Clínica</th>
                        <th className="p-2 border-b border-border text-right font-bold">Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReceipt.txs.map((tx) => (
                        <tr key={tx.id} className="hover:bg-muted/10 border-b border-border">
                          <td className="p-2">{formatDate(tx.competence_date)}</td>
                          <td className="p-2 font-medium text-foreground">{tx.patient?.full_name || tx.patient_name}</td>
                          <td className="p-2 text-right text-muted-foreground">R$ {tx.gross_amount.toFixed(2)}</td>
                          <td className="p-2 text-right text-muted-foreground">R$ {tx.gateway_fee_amount.toFixed(2)}</td>
                          <td className="p-2 text-right text-muted-foreground">R$ {tx.clinic_commission_amount.toFixed(2)}</td>
                          <td className="p-2 text-right font-bold text-brand">R$ {tx.professional_amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Resumo financeiro */}
              <div className="flex justify-between items-center border-t border-border pt-4">
                <div className="text-xs text-muted-foreground max-w-xs">
                  {selectedReceipt.batch.notes && <p className="italic">Nota: {selectedReceipt.batch.notes}</p>}
                </div>
                <div className="text-right space-y-1">
                  <div className="text-xs text-muted-foreground">Faturamento Bruto Lote: R$ {selectedReceipt.batch.total_gross.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">Retenções Totais: R$ {(selectedReceipt.batch.total_fees + selectedReceipt.batch.total_clinic).toFixed(2)}</div>
                  <div className="text-sm font-bold text-foreground">
                    Valor Líquido Transferido: <span className="text-brand text-lg">R$ {selectedReceipt.batch.total_professional.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6 border-t border-border pt-4">
              <button 
                onClick={() => window.print()}
                className="px-4 py-1.5 text-sm font-semibold bg-muted hover:bg-muted/80 text-muted-foreground rounded-lg transition-all flex items-center gap-1.5"
              >
                <Printer className="w-4 h-4" /> Imprimir / PDF
              </button>
              <button 
                onClick={() => setShowReceiptModal(false)}
                className="px-5 py-1.5 text-sm font-semibold bg-brand hover:bg-brand/90 text-white rounded-lg transition-all shadow-sm"
              >
                Ok, Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
